<template>
  <div class="modal fade show" style="display: block;" tabindex="-1">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fa fa-check-square text-success me-2"></i>
            Bulk Approve Closure Requests
          </h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        
        <div class="modal-body">
          <!-- Summary -->
          <div class="alert alert-info">
            <h6 class="alert-heading mb-2">
              <i class="fa fa-info-circle me-1"></i>
              Bulk Approval Summary
            </h6>
            <p class="mb-1">
              You are about to approve <strong>{{ requests.length }}</strong> closure request(s).
              This action will automatically close all associated subcategory logs.
            </p>
          </div>
          
          <!-- Requests List -->
          <div class="mb-3">
            <h6 class="text-primary mb-3">
              <i class="fa fa-list me-1"></i>
              Selected Requests
            </h6>
            
            <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
              <table class="table table-sm table-bordered">
                <thead class="table-light sticky-top">
                  <tr>
                    <th style="width: 5%;">#</th>
                    <th style="width: 20%;">Log Number</th>
                    <th style="width: 25%;">SubCategory</th>
                    <th style="width: 20%;">Requester</th>
                    <th style="width: 15%;">Priority</th>
                    <th style="width: 15%;">Created</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(request, index) in requests" :key="request._id">
                    <td>{{ index + 1 }}</td>
                    <td class="font-monospace">{{ request.subCategoryLogNo }}</td>
                    <td>{{ request.subCategoryId?.name || 'N/A' }}</td>
                    <td>
                      <div>
                        <strong>{{ request.requestedBy?.name || request.requestedBy?.employeeCode || 'N/A' }}</strong>
                        <br>
                        <small class="text-muted">{{ request.requestedBy?.employeeCode || 'N/A' }}</small>
                      </div>
                    </td>
                    <td>
                      <span 
                        class="badge"
                        :class="getPriorityClass(request.priority)"
                      >
                        {{ request.priority?.toUpperCase() || 'MEDIUM' }}
                      </span>
                      <br>
                      <span v-if="request.isUrgent" class="badge bg-warning mt-1">URGENT</span>
                    </td>
                    <td>
                      <small>{{ formatDate(request.createdAt) }}</small>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <!-- Warnings -->
          <div class="alert alert-warning mb-3">
            <h6 class="alert-heading">
              <i class="fa fa-exclamation-triangle me-1"></i>
              Important Warnings
            </h6>
            <ul class="mb-0">
              <li>All selected subcategory logs will be automatically closed</li>
              <li>This action cannot be undone</li>
              <li>Notifications will be sent to all requesters</li>
              <li>Admin approval will be recorded with your credentials</li>
            </ul>
          </div>
          
          <!-- High Priority/Urgent Requests Warning -->
          <div 
            v-if="hasHighPriorityOrUrgent" 
            class="alert alert-danger mb-3"
          >
            <h6 class="alert-heading">
              <i class="fa fa-exclamation-circle me-1"></i>
              High Priority/Urgent Requests Detected
            </h6>
            <p class="mb-1">
              This batch includes {{ highPriorityCount }} high priority and {{ urgentCount }} urgent request(s).
              Please ensure these have been thoroughly reviewed.
            </p>
          </div>
          
          <!-- Bulk Approval Comments -->
          <div class="mb-3">
            <label for="bulkApprovalComments" class="form-label">
              <i class="fa fa-comment me-1"></i>
              Bulk Approval Comments <span class="text-muted">(Optional)</span>
            </label>
            <textarea 
              id="bulkApprovalComments"
              v-model="bulkApprovalComments"
              class="form-control"
              rows="4"
              placeholder="Enter comments that will be applied to all approved requests..."
            ></textarea>
            <div class="form-text">
              These comments will be applied to all requests being approved in this batch.
            </div>
          </div>
          
          <!-- Quick Comment Templates -->
          <div class="mb-3">
            <label class="form-label">
              <i class="fa fa-list me-1"></i>
              Quick Templates
            </label>
            <div class="d-flex flex-wrap gap-2">
              <button 
                v-for="template in commentTemplates" 
                :key="template"
                type="button"
                class="btn btn-outline-secondary btn-sm"
                @click="addTemplate(template)"
              >
                {{ template }}
              </button>
            </div>
          </div>
          
          <!-- Processing Options -->
          <div class="mb-3">
            <div class="card">
              <div class="card-header">
                <h6 class="mb-0">
                  <i class="fa fa-cogs me-1"></i>
                  Processing Options
                </h6>
              </div>
              <div class="card-body">
                <div class="form-check mb-2">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    id="sendNotifications"
                    v-model="processingOptions.sendNotifications"
                  >
                  <label class="form-check-label" for="sendNotifications">
                    Send email notifications to requesters
                  </label>
                </div>
                
                <div class="form-check mb-2">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    id="updateDashboard"
                    v-model="processingOptions.updateDashboard"
                  >
                  <label class="form-check-label" for="updateDashboard">
                    Update dashboard metrics immediately
                  </label>
                </div>
                
                <div class="form-check">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    id="generateReport"
                    v-model="processingOptions.generateReport"
                  >
                  <label class="form-check-label" for="generateReport">
                    Generate bulk approval report
                  </label>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Confirmation -->
          <div class="form-check mb-3">
            <input 
              class="form-check-input" 
              type="checkbox" 
              id="confirmBulkApproval"
              v-model="confirmBulkApproval"
            >
            <label class="form-check-label fw-bold" for="confirmBulkApproval">
              I confirm that I want to bulk approve all {{ requests.length }} selected closure requests
            </label>
          </div>
        </div>
        
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            <i class="fa fa-times me-1"></i>
            Cancel
          </button>
          
          <button 
            type="button" 
            class="btn btn-success" 
            @click="handleBulkApprove"
            :disabled="!confirmBulkApproval || loading"
          >
            <i class="fa fa-spinner fa-spin me-1" v-if="loading"></i>
            <i class="fa fa-check-square me-1" v-else></i>
            {{ loading ? 'Processing...' : `Approve All ${requests.length} Requests` }}
          </button>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Modal backdrop -->
  <div class="modal-backdrop fade show"></div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';
import { formatDate, PRIORITY_CONFIG } from '@/services/subcategoryClosureService';

// Props
const props = defineProps({
  requests: {
    type: Array,
    required: true
  }
});

// Emits
const emit = defineEmits(['close', 'approve']);

// Reactive data
const bulkApprovalComments = ref('');
const confirmBulkApproval = ref(false);
const loading = ref(false);

// Processing options
const processingOptions = ref({
  sendNotifications: true,
  updateDashboard: true,
  generateReport: false
});

// Quick comment templates
const commentTemplates = [
  'Bulk approved - All requirements met',
  'Bulk approved - Batch processing completed',
  'Bulk approved - Documentation reviewed',
  'Bulk approved - Quality standards satisfied',
  'Bulk approved - Admin review completed'
];

// Computed properties
const hasHighPriorityOrUrgent = computed(() => {
  return props.requests.some(request => 
    request.priority === 'high' || request.isUrgent
  );
});

const highPriorityCount = computed(() => {
  return props.requests.filter(request => request.priority === 'high').length;
});

const urgentCount = computed(() => {
  return props.requests.filter(request => request.isUrgent).length;
});

// Methods
const getPriorityClass = (priority) => {
  return PRIORITY_CONFIG[priority]?.class || PRIORITY_CONFIG.medium.class;
};

const addTemplate = (template) => {
  if (bulkApprovalComments.value) {
    bulkApprovalComments.value += '\n' + template;
  } else {
    bulkApprovalComments.value = template;
  }
};

const handleBulkApprove = async () => {
  if (!confirmBulkApproval.value) {
    return;
  }
  
  loading.value = true;
  try {
    const requestIds = props.requests.map(request => request._id);
    emit('approve', requestIds, bulkApprovalComments.value, processingOptions.value);
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.modal {
  z-index: 1055;
}

.modal-backdrop {
  z-index: 1050;
}

.alert {
  border-radius: 0.375rem;
}

.alert-info {
  background-color: rgba(13, 202, 240, 0.1);
  border-color: rgba(13, 202, 240, 0.2);
  color: #055160;
}

.alert-warning {
  background-color: rgba(255, 193, 7, 0.1);
  border-color: rgba(255, 193, 7, 0.2);
  color: #664d03;
}

.alert-danger {
  background-color: rgba(220, 53, 69, 0.1);
  border-color: rgba(220, 53, 69, 0.2);
  color: #721c24;
}

.badge {
  font-size: 0.75rem;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.table-sm th,
.table-sm td {
  padding: 0.5rem;
  font-size: 0.875rem;
}

.font-monospace {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875rem;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
}

.form-check-input:checked {
  background-color: #3e5fce;
  border-color: #3e5fce;
}

.form-text {
  font-size: 0.875rem;
  color: #6c757d;
}

.fw-bold {
  font-weight: 600;
}

.text-primary {
  color: #3e5fce !important;
}

.text-success {
  color: #198754 !important;
}

.text-muted {
  color: #6c757d !important;
}

.card {
  border: 1px solid #dee2e6;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 0.75rem 1rem;
}

.card-body {
  padding: 1rem;
}

.table-responsive::-webkit-scrollbar {
  width: 6px;
}

.table-responsive::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.table-responsive::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>